// ABOUTME: Client-side photo compression for chat attachments.
// A phone photo is routinely 8-12 MB, which the 10 MB attachment limit rejects
// outright. Re-encoding through `createImageBitmap` -> canvas brings it under
// the limit while staying visibly sharp. Platform APIs only — no npm dependency.
//
// Every failure path returns the ORIGINAL file rather than throwing, so the
// caller's size check stays the single place that rejects an oversized upload.

/** Below this, re-encoding costs CPU and buys nothing. */
export const COMPRESSION_SKIP_BYTES = 1024 * 1024 // 1 MB

/** Compression stops as soon as the encode lands at or under this. */
export const COMPRESSION_TARGET_BYTES = 3 * 1024 * 1024 // 3 MB

/** Longest-edge cap. 3072px still prints and crops well; 12MP does not help. */
export const MAX_DIMENSION = 3072

/** Tried in order; the first encode at or under the target wins. */
const QUALITY_LADDER = [0.85, 0.72, 0.6, 0.5]

// GIF is deliberately absent: it is the one accepted type that can be animated,
// and a canvas re-encode would flatten it to a single frame.
const COMPRESSIBLE_MIMES = ['image/jpeg', 'image/png', 'image/webp']

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

let webpEncodeSupport: boolean | null = null

/** Canvas silently falls back to PNG for an unsupported encode type — probe once. */
function canEncodeWebp(): boolean {
  if (webpEncodeSupport === null) {
    try {
      const probe = document.createElement('canvas')
      probe.width = 1
      probe.height = 1
      webpEncodeSupport = probe.toDataURL('image/webp').startsWith('data:image/webp')
    } catch {
      webpEncodeSupport = false
    }
  }
  return webpEncodeSupport
}

/**
 * JPEG has no alpha channel, so a PNG or WebP source that carries transparency
 * would gain a black background. Keep those in a format that has one.
 */
function outputMime(file: File): string {
  if (file.type === 'image/jpeg') return 'image/jpeg'
  return canEncodeWebp() ? 'image/webp' : 'image/jpeg'
}

function isCompressible(file: File): boolean {
  return (
    COMPRESSIBLE_MIMES.includes(file.type) &&
    file.size > COMPRESSION_SKIP_BYTES &&
    typeof createImageBitmap === 'function' &&
    typeof document !== 'undefined'
  )
}

/**
 * `imageOrientation: 'from-image'` bakes the EXIF rotation into the bitmap, so a
 * portrait photo shot on a phone decodes upright instead of on its side. Older
 * engines reject the options bag outright rather than ignoring it — retry bare.
 */
async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return await createImageBitmap(file)
  }
}

function scaledSize(bitmap: ImageBitmap): { width: number; height: number } {
  const longestEdge = Math.max(bitmap.width, bitmap.height)
  if (longestEdge <= MAX_DIMENSION) return { width: bitmap.width, height: bitmap.height }
  const scale = MAX_DIMENSION / longestEdge
  return {
    width: Math.max(1, Math.round(bitmap.width * scale)),
    height: Math.max(1, Math.round(bitmap.height * scale)),
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality))
}

/** Swap the extension so the filename matches what we actually encoded. */
function renameForMime(filename: string, mime: string): string {
  const extension = EXTENSION_BY_MIME[mime]
  if (!extension) return filename
  return `${filename.replace(/\.[^./\\]+$/, '')}.${extension}`
}

/**
 * Compress `file` toward {@link COMPRESSION_TARGET_BYTES}, preserving EXIF
 * orientation and capping the longest edge at {@link MAX_DIMENSION}.
 *
 * Returns the original file untouched when it is already small, is a type we
 * must not re-encode, the platform lacks `createImageBitmap`, decoding fails
 * (HEIC and other formats the engine cannot read), or the re-encode came out no
 * smaller than the source. Never throws.
 */
export async function compressImage(file: File): Promise<File> {
  if (!isCompressible(file)) return file

  let bitmap: ImageBitmap
  try {
    bitmap = await decode(file)
  } catch {
    return file
  }

  try {
    const { width, height } = scaledSize(bitmap)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, width, height)

    const mime = outputMime(file)
    let smallest: Blob | null = null
    for (const quality of QUALITY_LADDER) {
      const blob = await toBlob(canvas, mime, quality)
      if (!blob) break
      smallest = blob
      if (blob.size <= COMPRESSION_TARGET_BYTES) break
    }

    if (!smallest || smallest.size >= file.size) return file

    return new File([smallest], renameForMime(file.name, smallest.type), {
      type: smallest.type,
      lastModified: file.lastModified,
    })
  } catch {
    return file
  } finally {
    bitmap.close?.()
  }
}
